import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { Check, Lock, X, Zap, Star, Rocket, ShieldCheck, Clock } from 'lucide-react';
import { updateCompanyPlan, setPlanRequestStatus } from '@/store/slices/companySlice';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { authApi } from '@/api/axios';

const PLANS = [
  {
    id: 'BASIC', name: 'Basic', price: '₹0', priceSub: 'Free forever',
    icon: Zap, color: '#6C6C70',
    features: ['Up to 5 employees','Manual attendance','Basic reports','Team chat'],
    locked: ['GPS/Selfie/QR attendance','Payroll processing','Expense management','Compliance filings','Recruitment ATS'],
  },
  {
    id: 'PRO', name: 'Pro', price: '₹149', priceSub: '/employee/month',
    icon: Star, color: '#007AFF', popular: true, requiresApproval: true,
    features: ['Up to 100 employees','1 company','All attendance methods','Full payroll','Expense + Khata ledger','Compliance filing tracker','Recruitment ATS pipeline','5 Lens kiosk devices','Advanced analytics'],
    locked: ['Multiple companies'],
  },
  {
    id: 'CUSTOM', name: 'Custom', price: 'Custom', priceSub: 'Contact sales',
    icon: Rocket, color: '#AF52DE', isSalesAssisted: true,
    features: ['Unlimited employees','Unlimited companies (workspace switcher)','Everything in Pro','API access','White-label branding','Dedicated support','Custom integrations'],
    locked: [],
  },
];

function ProPaymentModal({ plan, onClose, onConfirm }) {
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [activating, setActivating] = useState(false);
  const [success, setSuccess] = useState(false);
  const amount = 149;

  const handleActivate = async () => {
    setActivating(true);
    await new Promise(r => setTimeout(r, 1500));
    setSuccess(true);
    setTimeout(() => onConfirm(amount * 100), 900);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] shadow-[var(--shadow-xl)] w-full max-w-sm border border-[var(--border)] overflow-hidden fade-in">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Upgrading to</p>
            <h2 className="text-[20px] font-black text-[var(--text-primary)]">{plan.name} Plan</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><X size={18}/></button>
        </div>

        <div className="px-6 py-6 space-y-4">
          {success ? (
            <div className="flex flex-col items-center py-4 gap-3">
              <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Check size={28} className="text-green-600"/>
              </div>
              <p className="text-[16px] font-bold text-[var(--text-primary)]">Payment received!</p>
              <p className="text-[13px] text-[var(--text-secondary)] text-center">Pro unlocks once our team confirms — usually within 1 business hour.</p>
            </div>
          ) : (
            <>
              <div className="p-4 rounded-[12px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-start gap-3">
                <span className="text-[20px]">⚡</span>
                <div>
                  <p className="text-[13px] font-bold text-amber-700 dark:text-amber-400">Test Mode Active</p>
                  <p className="text-[12px] text-amber-600 dark:text-amber-500 mt-0.5">No real payment will be charged. In production, Razorpay handles billing.</p>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[12px] font-semibold text-[var(--text-primary)]">Card Number</label>
                <input value={cardNumber} maxLength={19} onChange={e=>setCardNumber(e.target.value.replace(/[^\d ]/g,''))} placeholder="4242 4242 4242 4242"
                  className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-[12px] font-semibold text-[var(--text-primary)]">Expiry</label>
                  <input value={expiry} maxLength={5} onChange={e=>setExpiry(e.target.value.replace(/[^\d/]/g,''))} placeholder="MM/YY"
                    className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[12px] font-semibold text-[var(--text-primary)]">CVV</label>
                  <input value={cvv} maxLength={3} type="password" onChange={e=>setCvv(e.target.value.replace(/\D/g,''))} placeholder="123"
                    className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
                </div>
              </div>

              <div className="rounded-[12px] bg-[var(--bg-muted)] p-4 space-y-2">
                <div className="flex justify-between text-[13px]">
                  <span className="text-[var(--text-secondary)]">Plan</span>
                  <span className="font-semibold text-[var(--text-primary)]">{plan.name}</span>
                </div>
                <div className="flex justify-between text-[13px]">
                  <span className="text-[var(--text-secondary)]">Price</span>
                  <span className="font-semibold text-[var(--text-primary)]">₹149/emp/mo</span>
                </div>
                <div className="flex justify-between text-[13px]">
                  <span className="text-[var(--text-secondary)]">Pay now (test)</span>
                  <span className="font-semibold text-[var(--text-primary)]">₹{amount}</span>
                </div>
              </div>

              <Button onClick={handleActivate} loading={activating} className="w-full" size="lg">
                {activating ? 'Processing…' : `Pay ₹${amount}`}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CustomRequestModal({ onClose, submitting, submitted }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e=>e.target===e.currentTarget && !submitting && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] shadow-[var(--shadow-xl)] w-full max-w-sm border border-[var(--border)] overflow-hidden fade-in px-6 py-8 flex flex-col items-center text-center gap-3">
        {submitted ? (
          <>
            <div className="w-14 h-14 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Check size={28} className="text-purple-600"/>
            </div>
            <p className="text-[16px] font-bold text-[var(--text-primary)]">Request sent!</p>
            <p className="text-[13px] text-[var(--text-secondary)]">Our team will reach out within 1 business hour to set up Custom and enable multi-company access.</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
            <p className="text-[14px] font-semibold text-[var(--text-primary)]">Sending your request…</p>
          </>
        )}
      </div>
    </div>
  );
}

export default function PlanPage() {
  const dispatch = useDispatch();
  const company = useSelector(s => s.company);
  const user = useSelector(s => s.auth.user);
  const accessToken = useSelector(s => s.auth.accessToken);
  const currentPlan = company.plan || 'BASIC';
  const [upgrading, setUpgrading] = useState(null);
  const [customRequest, setCustomRequest] = useState(null); // null | 'submitting' | 'submitted'
  const [saving, setSaving] = useState(false);

  const PLAN_ORDER = ['BASIC','PRO','CUSTOM'];
  const currentIdx = PLAN_ORDER.indexOf(currentPlan);
  const authHeaders = { headers: { Authorization: `Bearer ${accessToken}` } };

  const handleConfirmProUpgrade = async (dummyAmount) => {
    setSaving(true);
    try {
      await authApi.post('/v1/auth/plan-request', {
        plan: 'PRO', type: 'PRO_UPGRADE',
        dummy_payment_completed: true, dummy_payment_amount: dummyAmount,
      }, authHeaders);
      dispatch(setPlanRequestStatus('PENDING_PRO_APPROVAL'));
      setUpgrading(null);
    } catch (e) {
      console.error('Pro plan request failed:', e.message);
      toast.error('Could not submit your Pro request — please try again.');
    } finally { setSaving(false); }
  };

  const handleRequestCustom = async () => {
    setCustomRequest('submitting');
    try {
      await authApi.post('/v1/auth/plan-request', {
        plan: 'CUSTOM', type: 'CUSTOM_REGISTERED',
        contact_name: user?.name, contact_company_name: company?.name,
        contact_phone: user?.phone, contact_email: user?.email,
      }, authHeaders);
      dispatch(setPlanRequestStatus('PENDING_CUSTOM_APPROVAL'));
      setCustomRequest('submitted');
      setTimeout(() => setCustomRequest(null), 2200);
    } catch (e) {
      console.error('Custom plan request failed:', e.message);
      toast.error('Could not submit your Custom plan request — please try again.');
      setCustomRequest(null);
    }
  };

  const handleSelectPlan = (plan) => {
    if (plan.id === 'PRO') setUpgrading(plan);
    else if (plan.id === 'CUSTOM') handleRequestCustom();
  };

  return (
    <div className="space-y-5 fade-in max-w-4xl">
      <PageHeader
        title="Plan & Billing"
        subtitle={`Current plan: ${PLANS.find(p=>p.id===currentPlan)?.name || 'Basic'}`}
        breadcrumb={[{label:'Settings',href:'/settings'},{label:'Plan & Billing'}]}
      />

      {company.plan_request_status === 'PENDING_PRO_APPROVAL' && (
        <div className="card border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 flex items-center gap-3 p-4">
          <Clock size={16} className="text-amber-600 dark:text-amber-400 shrink-0" />
          <p className="text-[13px] text-amber-700 dark:text-amber-400">Your Pro upgrade is under review — we'll confirm within 1 business hour.</p>
        </div>
      )}
      {company.plan_request_status === 'PENDING_CUSTOM_APPROVAL' && (
        <div className="card border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 flex items-center gap-3 p-4">
          <Clock size={16} className="text-purple-600 dark:text-purple-400 shrink-0" />
          <p className="text-[13px] text-purple-700 dark:text-purple-400">Your Custom plan request is with our team — we'll call within 1 business hour.</p>
        </div>
      )}

      {/* Current plan banner */}
      <div className="card border-2 border-[var(--accent)] bg-[var(--accent)]/5 p-5 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-wide text-[var(--accent)]">Your current plan</p>
            <p className="text-[20px] font-black text-[var(--text-primary)] mt-0.5">
              {PLANS.find(p=>p.id===currentPlan)?.name}
            </p>
            <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">
              {PLANS.find(p=>p.id===currentPlan)?.features.slice(0,3).join(' · ')}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[24px] font-black text-[var(--text-primary)]">{PLANS.find(p=>p.id===currentPlan)?.price}</p>
            <p className="text-[11px] text-[var(--text-secondary)]">{PLANS.find(p=>p.id===currentPlan)?.priceSub}</p>
          </div>
        </div>
        {company.plan_expires_at && (
          <div className="mt-3 pt-3 border-t border-[var(--accent)]/15 flex items-center gap-2">
            <Clock size={13} className="text-[var(--text-tertiary)] shrink-0" />
            <p className="text-[12px] text-[var(--text-secondary)]">
              {new Date(company.plan_expires_at) > new Date()
                ? <>Renews on <strong className="text-[var(--text-primary)]">{new Date(company.plan_expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</strong> — billed monthly</>
                : <span className="text-[var(--danger)] font-semibold">Plan expired on {new Date(company.plan_expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} — renew to keep access</span>}
            </p>
          </div>
        )}
        {currentPlan === 'BASIC' && !company.plan_expires_at && (
          <div className="mt-3 pt-3 border-t border-[var(--accent)]/15 flex items-center gap-2">
            <Check size={13} className="text-green-500 shrink-0" />
            <p className="text-[12px] text-[var(--text-secondary)]">Free forever — no expiration, no card required.</p>
          </div>
        )}
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {PLANS.map((plan) => {
          const Icon = plan.icon;
          const isCurrent = plan.id === currentPlan;
          const isUpgrade = PLAN_ORDER.indexOf(plan.id) > currentIdx;

          return (
            <div key={plan.id} className={cn('card flex flex-col gap-3 border-2 transition-all p-5',
              isCurrent ? 'border-[var(--accent)]' : 'border-transparent',
              plan.popular && !isCurrent ? 'ring-1 ring-[var(--accent)]/30' : ''
            )}>
              {plan.popular && !isCurrent && (
                <div className="text-center -mt-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--accent)] bg-[var(--accent)]/10 px-2 py-0.5 rounded-full">Most Popular</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-[8px] flex items-center justify-center" style={{backgroundColor:`${plan.color}18`}}>
                  <Icon size={16} style={{color:plan.color}}/>
                </div>
                <div>
                  <p className="text-[14px] font-bold text-[var(--text-primary)]">{plan.name}</p>
                  <p className="text-[11px] text-[var(--text-tertiary)]">{plan.priceSub}</p>
                </div>
              </div>

              <p className="text-[22px] font-black text-[var(--text-primary)]">{plan.price}</p>

              <div className="space-y-1.5 flex-1">
                {plan.features.map(f=>(
                  <div key={f} className="flex items-start gap-1.5">
                    <Check size={12} className="mt-0.5 shrink-0 text-[var(--success)]"/>
                    <span className="text-[12px] text-[var(--text-secondary)]">{f}</span>
                  </div>
                ))}
                {plan.locked.map(f=>(
                  <div key={f} className="flex items-start gap-1.5">
                    <Lock size={12} className="mt-0.5 shrink-0 text-[var(--text-tertiary)]"/>
                    <span className="text-[12px] text-[var(--text-tertiary)]">{f}</span>
                  </div>
                ))}
              </div>

              {isCurrent ? (
                <div className="h-9 flex items-center justify-center rounded-[8px] bg-[var(--accent)]/10 text-[12px] font-semibold text-[var(--accent)]">
                  Current Plan
                </div>
              ) : isUpgrade ? (
                <Button size="sm" onClick={()=>handleSelectPlan(plan)} className="w-full">
                  {plan.isSalesAssisted ? 'Request Custom' : `Upgrade to ${plan.name}`}
                </Button>
              ) : (
                <div className="h-9 flex items-center justify-center rounded-[8px] bg-[var(--bg-muted)] text-[12px] text-[var(--text-tertiary)]">
                  Included in your plan
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[12px] text-[var(--text-tertiary)] text-center">
        Basic is free forever · Pro activates after a quick team review · Custom is sales-assisted
      </p>

      {upgrading && (
        <ProPaymentModal
          plan={upgrading}
          onClose={() => setUpgrading(null)}
          onConfirm={handleConfirmProUpgrade}
        />
      )}

      {customRequest && (
        <CustomRequestModal
          submitting={customRequest === 'submitting'}
          submitted={customRequest === 'submitted'}
          onClose={() => setCustomRequest(null)}
        />
      )}
    </div>
  );
}
